import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { scrollToSection, scrollToTop } from '../utils/scrollHandlers';

const CustomFooter = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const isHomePage = location.pathname === '/';
  
  const footerLinks = {
    product: [
      { name: 'Features', href: '/#features' },
      { name: 'NFC Cards', href: '/nfc-cards' },
      { name: 'Personal Card', href: '/personal-card' },
      { name: 'Business Card', href: '/business-card' },
      { name: 'Enterprise', href: '/enterprise' },
      { name: 'Custom Cards', href: '/custom-cards'}  
    ],
    company: [
      { name: 'About Us', href: '/about' },
      { name: 'Blog', href: '/blog' },
      { name: 'Contact', href: '/contact' }
    ],
    support: [
      { name: 'Help Center', href: '/help' },
      { name: 'Privacy Policy', href: '/privacy' },
      { name: 'Terms of Service', href: '/terms' },
      { name: 'Cookie Policy', href: '/cookies' }
    ]
  };

  const socialLinks = [
    { icon: ['fab', 'instagram'], href: 'https://www.instagram.com/am_cardmate?igsh=MTh5cHNrdmVrZTE4ZQ==' },
    { icon: ['fab', 'linkedin-in'], href: 'https://www.linkedin.com/company/am-cardmate' }
  ];

  const handleLinkClick = (e, href) => {
    e.preventDefault();
    
    try {
      sessionStorage.setItem('internalNavigation', 'true');
    } catch (error) {
      console.warn('SessionStorage not available');
    }
    
    if (href.startsWith('#')) {
      scrollToSection(href);
    } else if (href.startsWith('/#')) {
      if (isHomePage) {
        scrollToSection(href.substring(1));
      } else {
        navigate('/');
        setTimeout(() => {
          scrollToSection(href.substring(1));
        }, 100);
      }
    } else {
      if (location.pathname === href) {
        scrollToTop();
      } else {
        navigate(href);
      }
    }
  };

  return (
    <footer className="bg-dark text-white pt-20 pb-8">
      <div className="container mx-auto px-6">
        <div className="flex flex-col lg:flex-row justify-between mb-16 gap-12">
          {/* Brand Section */}
          <div className="lg:max-w-sm">
            <Link 
              to="/" 
              className="inline-block font-display text-h3 font-extrabold mb-6 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent"
              onClick={(e) => handleLinkClick(e, '/')}
            >
              AM CARDMATE
            </Link>
            
            <p className="text-body text-white/70 mb-8 leading-relaxed">
              Modern digital business cards for the digital age. Connect, share, and grow your network with our innovative NFC card technology.
            </p>
            
            {/* Social Links */}
            <div className="flex gap-4">
              {socialLinks.map((link, index) => (
                <a 
                  key={index}
                  href={link.href}
                  className="w-[45px] h-[45px] rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-gradient-blue hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FontAwesomeIcon icon={link.icon} />
                </a>
              ))}
            </div>
          </div>
          
          {/* Links Sections */}
          <div className="flex flex-wrap gap-16">
            {/* Product Links */}
            <div className="w-full sm:w-auto">
              <h4 className="text-h5 mb-6 pb-3 relative">
                Product
                <span className="absolute bottom-0 left-0 w-10 h-0.5 bg-gradient-blue"></span>
              </h4>
              
              <ul>
                {footerLinks.product.map((link, index) => (
                  <li key={index} className="mb-4">
                    {link.href.includes('#') ? (
                      <a 
                        href={link.href} 
                        className="text-body-sm text-white/70 hover:text-white flex items-center gap-2 hover:translate-x-1 transition-all duration-200"
                        onClick={(e) => handleLinkClick(e, link.href)}
                      >
                        <FontAwesomeIcon icon="chevron-right" className="text-xs text-primary-blue" />
                        {link.name}
                      </a>
                    ) : (
                      <Link 
                        to={link.href} 
                        className="text-body-sm text-white/70 hover:text-white flex items-center gap-2 hover:translate-x-1 transition-all duration-200"
                        onClick={(e) => handleLinkClick(e, link.href)}
                      >
                        <FontAwesomeIcon icon="chevron-right" className="text-xs text-primary-blue" />
                        {link.name}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Company Links */}
            <div className="w-full sm:w-auto">
              <h4 className="text-h5 mb-6 pb-3 relative">
                Company
                <span className="absolute bottom-0 left-0 w-10 h-0.5 bg-gradient-blue"></span>
              </h4>
              
              <ul>
                {footerLinks.company.map((link, index) => (
                  <li key={index} className="mb-4">
                    <Link 
                      to={link.href} 
                      className="text-body-sm text-white/70 hover:text-white flex items-center gap-2 hover:translate-x-1 transition-all duration-200"
                      onClick={(e) => handleLinkClick(e, link.href)}
                    >
                      <FontAwesomeIcon icon="chevron-right" className="text-xs text-primary-blue" />
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Support Links */}
            <div className="w-full sm:w-auto">
              <h4 className="text-h5 mb-6 pb-3 relative">
                Support
                <span className="absolute bottom-0 left-0 w-10 h-0.5 bg-gradient-blue"></span>
              </h4>
              
              <ul>
                {footerLinks.support.map((link, index) => (
                  <li key={index} className="mb-4">
                    <Link 
                      to={link.href} 
                      className="text-body-sm text-white/70 hover:text-white flex items-center gap-2 hover:translate-x-1 transition-all duration-200"
                      onClick={(e) => handleLinkClick(e, link.href)}
                    >
                      <FontAwesomeIcon icon="chevron-right" className="text-xs text-primary-blue" />
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        
        {/* Copyright */}
        <div className="text-center text-body-sm text-white/50 pt-8 border-t border-white/10">
          <p>
            &copy; {new Date().getFullYear()} AM CARDMATE. All rights reserved. | Website by{' '}
            <a 
              href="https://www.alinamakovei.co.uk/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-primary-blue hover:text-white transition-colors duration-200"
            >
              AM Digital
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default CustomFooter;